// Clan + share bindings. Mirrors iOS ClansEndpoints + /clans/*.
import { api } from "./client";
import type {
  ClanCreatePayload,
  ClanEventRead,
  ClanFeedResponse,
  ClanInviteActionResponse,
  ClanInviteCreatePayload,
  ClanInviteRead,
  ClanJoinPreview,
  ClanMemberRead,
  ClanMemberUpdatePayload,
  ClanRead,
  ClanRuleCreatePayload,
  ClanRuleRead,
  ClanRuleUpdatePayload,
  ClanSearchResult,
  ClanShareCreatePayload,
  ClanShareRead,
  ClanUpdatePayload,
  JoinCode,
} from "./community-types";

export function listClans(): Promise<ClanRead[]> {
  return api.get<ClanRead[]>("/clans/");
}

export function searchClans(q: string): Promise<ClanSearchResult[]> {
  return api.get<ClanSearchResult[]>("/clans/search", { query: { q } });
}

export function createClan(payload: ClanCreatePayload): Promise<ClanRead> {
  return api.post<ClanRead>("/clans/", payload);
}

export function getClan(clanId: string): Promise<ClanRead> {
  return api.get<ClanRead>(`/clans/${encodeURIComponent(clanId)}`);
}

export function updateClan(clanId: string, payload: ClanUpdatePayload): Promise<ClanRead> {
  return api.patch<ClanRead>(`/clans/${encodeURIComponent(clanId)}`, payload);
}

export function getClanFeed(clanId: string): Promise<ClanFeedResponse> {
  return api.get<ClanFeedResponse>(`/clans/${encodeURIComponent(clanId)}/feed`);
}

export function listClanMembers(clanId: string): Promise<ClanMemberRead[]> {
  return api.get<ClanMemberRead[]>(`/clans/${encodeURIComponent(clanId)}/members`);
}

/** The class activity feed (newest first). */
export function listClanEvents(clanId: string): Promise<ClanEventRead[]> {
  return api.get<ClanEventRead[]>(`/clans/${encodeURIComponent(clanId)}/events`);
}

// ── Invites (targeted) ───────────────────────────────────────────────────────

/** Invite a user to a class. They must accept before joining (no direct add). */
export function sendClanInvite(
  clanId: string,
  payload: ClanInviteCreatePayload,
): Promise<ClanInviteRead> {
  return api.post<ClanInviteRead>(`/clans/${encodeURIComponent(clanId)}/invites`, payload);
}

/** Pending invites for a class (admin view). */
export function listClanInvites(clanId: string): Promise<ClanInviteRead[]> {
  return api.get<ClanInviteRead[]>(`/clans/${encodeURIComponent(clanId)}/invites`);
}

/** Revoke a pending invite (admin). */
export function revokeClanInvite(clanId: string, inviteId: string): Promise<void> {
  return api.delete<void>(
    `/clans/${encodeURIComponent(clanId)}/invites/${encodeURIComponent(inviteId)}`,
  );
}

/** The caller's own incoming pending invites across all classes. */
export function listMyClanInvites(): Promise<ClanInviteRead[]> {
  return api.get<ClanInviteRead[]>("/clans/invites");
}

export function acceptClanInvite(inviteId: string): Promise<ClanInviteActionResponse> {
  return api.post<ClanInviteActionResponse>(
    `/clans/invites/${encodeURIComponent(inviteId)}/accept`,
    {},
  );
}

export function declineClanInvite(inviteId: string): Promise<ClanInviteActionResponse> {
  return api.post<ClanInviteActionResponse>(
    `/clans/invites/${encodeURIComponent(inviteId)}/decline`,
    {},
  );
}

// ── Join code (link / QR) ────────────────────────────────────────────────────

/** Fetch (lazily generating) the class's shareable join code + link. Admin. */
export function getJoinCode(clanId: string): Promise<JoinCode> {
  return api.get<JoinCode>(`/clans/${encodeURIComponent(clanId)}/join-code`);
}

/** Rotate the join code — old link/QR stops working. Admin. */
export function rotateJoinCode(clanId: string): Promise<JoinCode> {
  return api.post<JoinCode>(`/clans/${encodeURIComponent(clanId)}/join-code/rotate`, {});
}

/** Resolve a join code to a class preview (shown before joining). */
export function previewJoinCode(code: string): Promise<ClanJoinPreview> {
  return api.get<ClanJoinPreview>(`/clans/join/${encodeURIComponent(code)}`);
}

/** Instant-join a class via its code. */
export function joinByCode(code: string): Promise<ClanRead> {
  return api.post<ClanRead>(`/clans/join/${encodeURIComponent(code)}`, {});
}

// ── Share reporting (§14.5 flag-for-review) ──────────────────────────────────

export type ReportReason =
  | "spam"
  | "harassment"
  | "sexual"
  | "violence"
  | "self_harm"
  | "hate"
  | "other";

export interface ClanShareReport {
  id: string;
  share_id: string;
  resource_type: string;
  resource_title: string | null;
  reason: string;
  detail: string | null;
  reporter_display_name: string;
  created_at: string;
}

/** Report a shared resource. Recorded for the Teacher to review; stays visible. */
export function reportClanShare(
  shareId: string,
  reason: ReportReason,
  detail?: string,
): Promise<{ id: string; status: string }> {
  return api.post<{ id: string; status: string }>("/reports/clan-shares", {
    share_id: shareId,
    reason,
    detail: detail ?? null,
  });
}

/** Teacher/TA: open reports against shares in this class. */
export function listClanShareReports(clanId: string): Promise<ClanShareReport[]> {
  return api.get<ClanShareReport[]>(`/clans/${encodeURIComponent(clanId)}/share-reports`);
}

/** Update a member's role and/or title. Role changes are owner-only on the
 *  backend; title changes are admin+. Pass only what you're changing. */
export function updateClanMember(
  clanId: string,
  userId: string,
  payload: ClanMemberUpdatePayload,
): Promise<ClanMemberRead> {
  return api.patch<ClanMemberRead>(
    `/clans/${encodeURIComponent(clanId)}/members/${encodeURIComponent(userId)}`,
    payload,
  );
}

export function removeClanMember(clanId: string, userId: string): Promise<void> {
  return api.delete<void>(
    `/clans/${encodeURIComponent(clanId)}/members/${encodeURIComponent(userId)}`,
  );
}

export function listClanShares(clanId: string): Promise<ClanShareRead[]> {
  return api.get<ClanShareRead[]>(`/clans/${encodeURIComponent(clanId)}/shares`);
}

export function createClanShare(
  clanId: string,
  payload: ClanShareCreatePayload,
): Promise<ClanShareRead> {
  return api.post<ClanShareRead>(`/clans/${encodeURIComponent(clanId)}/shares`, {
    permissions: "view",
    source_material_disclaimer: false,
    ...payload,
  });
}

// ── Rules ─────────────────────────────────────────────────────────────────────

export function listClanRules(clanId: string): Promise<ClanRuleRead[]> {
  return api.get<ClanRuleRead[]>(`/clans/${encodeURIComponent(clanId)}/rules`);
}

export function createClanRule(clanId: string, payload: ClanRuleCreatePayload): Promise<ClanRuleRead> {
  return api.post<ClanRuleRead>(`/clans/${encodeURIComponent(clanId)}/rules`, payload);
}

export function updateClanRule(
  clanId: string,
  ruleId: string,
  payload: ClanRuleUpdatePayload,
): Promise<ClanRuleRead> {
  return api.patch<ClanRuleRead>(
    `/clans/${encodeURIComponent(clanId)}/rules/${encodeURIComponent(ruleId)}`,
    payload,
  );
}

export function deleteClanRule(clanId: string, ruleId: string): Promise<void> {
  return api.delete<void>(
    `/clans/${encodeURIComponent(clanId)}/rules/${encodeURIComponent(ruleId)}`,
  );
}

export function acceptClanRules(clanId: string, ruleVersions: Record<string, number>): Promise<void> {
  return api.post<void>(`/clans/${encodeURIComponent(clanId)}/rules/accept`, {
    rule_versions: ruleVersions,
  });
}
